import classNames from 'classnames'
import * as React from 'react'
import type {ContextType} from 'react'

import stableSort from '../../../../utils/stableSort'
import {
  PipelinesTarget,
  PipelinesTargetContext,
} from '../../../pages/PipelinesPages/contexts/targetContext'
import type {LogFilter, FullLogScrollBarInfo} from '../BuildLog.types'
import {
  INITIAL_NEXT_MESSAGES_COUNT,
  INITIAL_PREVIOUS_MESSAGES_COUNT,
  EXPAND_MESSAGE_COUNT,
  getKeyForBuildLogData,
  getMessageExpandableChildren,
} from '../BuildLog.utils'
import BuildLogInfo from '../BuildLogInfo/BuildLogInfo'
import FullBuildLogMessages from '../BuildLogMessages/FullBuildLogMessages'

import BuildLogCompactHeader from './BuildLogCompactHeader/BuildLogCompactHeader'
import BuildLogHeader from './BuildLogHeader/BuildLogHeader'
import BuildLogNavigation from './BuildLogNavigation/BuildLogNavigation.container'
import {BuildIdContext, TargetContext} from './FullBuildLog.context'
import type {Props} from './FullBuildLog.types'

import styles from './FullBuildLog.css'

const THROTTLE_UPDATE_INTERVAL = 5000

class FullBuildLog extends React.PureComponent<Props> {
  static contextType = PipelinesTargetContext
  declare context: ContextType<typeof PipelinesTargetContext>

  componentDidMount(): void {
    this.loadInitialMessages()
  }

  componentDidUpdate(prevProps: Props): void {
    const {buildId, running} = this.props

    if (buildId !== prevProps.buildId) {
      this.loadInitialMessages()
    } else if (prevProps.running && !running) {
      this.loadLastMessages(INITIAL_NEXT_MESSAGES_COUNT, true)
    }
  }

  loadInitialMessages: () => void = () => {
    const {focusLine, ready, loading, expandAll, queued, filter}: Props = this.props

    if (!ready && loading) {
      return
    }

    const autoFilter = queued && filter == null ? 'debug' : null
    this.fetchMessages({
      count: [INITIAL_NEXT_MESSAGES_COUNT, INITIAL_PREVIOUS_MESSAGES_COUNT],
      index: focusLine ?? 0,
      invalidate: true,
      filter: filter ?? autoFilter,
      expandFailures: true,
      expandAll,
    })
  }

  loadMoreMessages = (index: number, count: number, throttle = false): void => {
    const {messagesLoadState} = this.props
    const loadState = messagesLoadState != null ? messagesLoadState[index.toString()] : null

    if (
      loadState != null &&
      (loadState.loading ||
        (throttle &&
          loadState.lastLoadedTime != null &&
          loadState.lastLoadedTime > Date.now() - THROTTLE_UPDATE_INTERVAL))
    ) {
      return
    }

    this.fetchMessages({
      index,
      count,
      mergeData: true,
    })
  }

  loadLastMessages: (count: number, force?: boolean) => void = (count: number, force?: boolean) => {
    const {messagesLoadState} = this.props
    const loadState = messagesLoadState?.['-1']

    if (
      force !== true &&
      loadState != null &&
      (loadState.loading ||
        (loadState.lastLoadedTime != null &&
          loadState.lastLoadedTime > Date.now() - THROTTLE_UPDATE_INTERVAL))
    ) {
      return
    }

    this.fetchMessages({
      count: [0, -count],
      index: -1,
      mergeData: true,
    })
  }

  getNextExpandState = (index: number, expand: boolean, toggleSubtree = false) => {
    const {expandState, expandAll, expandableChildrenMap} = this.props
    const appendToState = expandAll ? !expand : expand

    if (!toggleSubtree) {
      return appendToState
        ? expandState != null
          ? stableSort([...expandState, index], (a, b) => a - b)
          : [index]
        : expandState?.filter(i => i !== index) ?? null
    }

    const expandableChildren = getMessageExpandableChildren(expandableChildrenMap, index)

    return appendToState
      ? expandState != null
        ? stableSort(
            Array.from(new Set([...expandableChildren, ...expandState, index])),
            (a, b) => a - b,
          )
        : [index]
      : expandState?.filter(i => i !== index && !expandableChildren.includes(i)) ?? null
  }

  onMessageToggle: (index: number, expand: boolean, withFocus?: boolean) => void = (
    index: number,
    expand: boolean,
    withFocus?: boolean,
  ) => {
    const {focusLine, updateLogState, target} = this.props
    const nextExpandState = this.getNextExpandState(index, expand)

    updateLogState({
      focusLine: withFocus === true ? index : focusLine,
      expandState: nextExpandState,
      target,
    })

    if (expand) {
      this.fetchMessages({
        index,
        expandState: nextExpandState,
        count: EXPAND_MESSAGE_COUNT,
        mergeData: true,
      })
    }
  }

  onShowMessages: (options: {
    firstDate: string | null | undefined
    firstId: number | null | undefined
    lastDate: string | null | undefined
    lastId: number | null | undefined
  }) => void = (options: {
    firstDate: string | null | undefined
    lastDate: string | null | undefined
    firstId: number | null | undefined
    lastId: number | null | undefined
  }) => {
    const {onShowMessages, target} = this.props
    onShowMessages({
      target,
      ...options,
    })
  }

  onMessageSelect: (index: number) => void = (index: number) => {
    const {updateLogState, target} = this.props
    updateLogState({
      focusLine: index,
      target,
    })
  }

  navigateToMessage: (index: number) => void = (index: number) => {
    this.fetchMessages({
      invalidate: true,
      count: index < 0 ? [0, -INITIAL_NEXT_MESSAGES_COUNT] : INITIAL_NEXT_MESSAGES_COUNT,
      index,
    })
  }

  toggleMessageSubtree = (index: number, expand: boolean) => {
    const {expandState, target, updateLogState} = this.props

    if (expand) {
      this.fetchMessages({
        index,
        expandState,
        expandSubtree: index,
        mergeData: true,
      })

      return
    }

    const nextExpandState = this.getNextExpandState(index, expand, true)
    updateLogState({
      target,
      focusLine: index,
      expandState: nextExpandState,
    })
  }

  fetchMessages = ({
    index,
    invalidate,
    expandState,
    expandAll,
    expandSubtree,
    expandFailures,
    count,
    mergeData,
    filter,
  }: {
    count?: number | ReadonlyArray<number>
    expandAll?: boolean
    expandSubtree?: number
    expandFailures?: boolean
    expandState?: ReadonlyArray<number> | null | undefined
    filter?: LogFilter | null | undefined
    index?: number
    invalidate?: boolean
    mergeData?: boolean
  }) => {
    const {
      buildId,
      expandState: savedExpandState,
      expandAll: savedExpandAll,
      filter: savedFilter,
      fetchMessages,
      logView,
      target,
    } = this.props

    if (buildId != null) {
      fetchMessages({
        buildLogKey: getKeyForBuildLogData({
          type: 'full',
          target,
        }),
        buildId,
        invalidate,
        target,
        mergeData,
        options: {
          expandState: expandState ?? savedExpandState,
          expandAll: expandAll ?? savedExpandAll,
          filter: filter ?? savedFilter,
          logAnchor: index,
          expandFailures,
          expandSubtree,
          logView,
          count,
        },
      })
    }
  }

  setScrollBarInfo: (info: FullLogScrollBarInfo) => void = (info: FullLogScrollBarInfo) => {
    const {setScrollBarInfo, target} = this.props
    setScrollBarInfo(target, info)
  }

  renderMessages(): React.ReactNode {
    const {
      ready,
      messages,
      buildId,
      holeIndexes,
      focusLine,
      running,
      queued,
      expandStateSet,
      expandAll,
      softWrapLines,
      relativeTime,
      maxMessageLength,
      searchQuery,
      searchFoundId,
      searchMatchPosition,
      lastLoadingDirection,
      isAutodetectTimeZone,
      logView,
      target,
    } = this.props

    if (!ready) {
      return <BuildLogInfo type="loading" target={target} />
    }

    if (buildId == null || messages == null || (!running && messages.length === 0)) {
      return <BuildLogInfo type="empty" target={target} />
    }

    return (
      <div className={styles.messagesWrapper}>
        <FullBuildLogMessages
          messages={messages}
          className={styles.messages}
          holeIndexes={holeIndexes}
          buildId={buildId}
          onMessageToggle={this.onMessageToggle}
          onMessageSelect={this.onMessageSelect}
          onShowMessages={this.onShowMessages}
          loadMoreMessages={this.loadMoreMessages}
          loadLastMessages={this.loadLastMessages}
          setScrollBarInfo={this.setScrollBarInfo}
          navigateToMessage={this.navigateToMessage}
          toggleMessageSubtree={this.toggleMessageSubtree}
          focusLine={focusLine}
          running={running}
          queued={queued}
          expandStateSet={expandStateSet}
          expandAll={expandAll}
          softWrapLines={softWrapLines}
          relativeTime={relativeTime}
          maxMessageLength={maxMessageLength}
          searchQuery={searchQuery}
          searchFoundId={searchFoundId}
          searchMatchPosition={searchMatchPosition}
          target={target}
          pipelinesTarget={this.context}
          lastLoadingDirection={lastLoadingDirection}
          isAutodetectTimeZone={isAutodetectTimeZone}
          logView={logView}
        />
      </div>
    )
  }

  render(): React.ReactNode {
    const {buildId, target, className, messages} = this.props

    const Header =
      this.context === PipelinesTarget.JOB_DETAILS_POPUP ||
      this.context === PipelinesTarget.JOB_DETAILS_SIDEBAR
        ? BuildLogCompactHeader
        : BuildLogHeader

    return (
      <BuildIdContext.Provider value={buildId}>
        <TargetContext.Provider value={target}>
          <div className={classNames(styles.fullBuildLog, className)}>
            <Header buildId={buildId} />
            {this.renderMessages()}
            <BuildLogNavigation
              target={target}
              startMessageId={messages[0]?.id}
              finishMessageId={messages[messages.length - 1]?.id}
            />
          </div>
        </TargetContext.Provider>
      </BuildIdContext.Provider>
    )
  }
}

export default FullBuildLog
